'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, ChevronDown, X, Search, Loader2 } from 'lucide-react';
import { getProvinces, getDistricts, getNeighborhoods, type Province, type District, type Neighborhood } from '@/lib/locations';

interface LocationPickerProps {
  selectedProvince: string;
  selectedDistrict: string;
  selectedNeighborhood?: string;
  onProvinceChange: (value: string) => void;
  onDistrictChange: (value: string) => void;
  onNeighborhoodChange?: (value: string) => void;
  compact?: boolean;
  className?: string;
}

export default function LocationPicker({
  selectedProvince,
  selectedDistrict,
  selectedNeighborhood = '',
  onProvinceChange,
  onDistrictChange,
  onNeighborhoodChange,
  compact = false,
  className = '',
}: LocationPickerProps) {
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [neighborhoods, setNeighborhoods] = useState<Neighborhood[]>([]);
  const [loadingProvinces, setLoadingProvinces] = useState(false);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingNeighborhoods, setLoadingNeighborhoods] = useState(false);

  const [provinceSearch, setProvinceSearch] = useState('');
  const [districtSearch, setDistrictSearch] = useState('');
  const [neighborhoodSearch, setNeighborhoodSearch] = useState('');

  const [showProvinceDropdown, setShowProvinceDropdown] = useState(false);
  const [showDistrictDropdown, setShowDistrictDropdown] = useState(false);
  const [showNeighborhoodDropdown, setShowNeighborhoodDropdown] = useState(false);

  const provinceRef = useRef<HTMLDivElement>(null);
  const districtRef = useRef<HTMLDivElement>(null);
  const neighborhoodRef = useRef<HTMLDivElement>(null);

  // Load provinces on mount
  useEffect(() => {
    setLoadingProvinces(true);
    getProvinces().then((data) => {
      setProvinces(data);
      setLoadingProvinces(false);
    });
  }, []);

  // Load districts when province changes
  useEffect(() => {
    if (!selectedProvince) {
      setDistricts([]);
      setNeighborhoods([]);
      return;
    }
    const province = provinces.find((p) => p.name === selectedProvince || p.slug === selectedProvince);
    if (!province) return;
    setLoadingDistricts(true);
    getDistricts(province.id).then((data) => {
      setDistricts(data);
      setLoadingDistricts(false);
    });
  }, [selectedProvince, provinces]);

  // Load neighborhoods when district changes
  useEffect(() => {
    if (!selectedDistrict || !onNeighborhoodChange) {
      setNeighborhoods([]);
      return;
    }
    const district = districts.find((d) => d.name === selectedDistrict || d.slug === selectedDistrict);
    if (!district) return;
    setLoadingNeighborhoods(true);
    getNeighborhoods(district.id).then((data) => {
      setNeighborhoods(data);
      setLoadingNeighborhoods(false);
    });
  }, [selectedDistrict, districts, onNeighborhoodChange]);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (provinceRef.current && !provinceRef.current.contains(e.target as Node)) {
        setShowProvinceDropdown(false);
      }
      if (districtRef.current && !districtRef.current.contains(e.target as Node)) {
        setShowDistrictDropdown(false);
      }
      if (neighborhoodRef.current && !neighborhoodRef.current.contains(e.target as Node)) {
        setShowNeighborhoodDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filteredProvinces = provinces.filter((p) =>
    p.name.toLowerCase().includes(provinceSearch.toLowerCase())
  );
  const filteredDistricts = districts.filter((d) =>
    d.name.toLowerCase().includes(districtSearch.toLowerCase())
  );
  const filteredNeighborhoods = neighborhoods.filter((n) =>
    n.name.toLowerCase().includes(neighborhoodSearch.toLowerCase())
  );

  const handleProvinceSelect = (province: Province) => {
    onProvinceChange(province.name);
    onDistrictChange('');
    if (onNeighborhoodChange) onNeighborhoodChange('');
    setProvinceSearch('');
    setDistrictSearch('');
    setNeighborhoodSearch('');
    setShowProvinceDropdown(false);
  };

  const handleDistrictSelect = (district: District) => {
    onDistrictChange(district.name);
    if (onNeighborhoodChange) onNeighborhoodChange('');
    setDistrictSearch('');
    setNeighborhoodSearch('');
    setShowDistrictDropdown(false);
  };

  const handleNeighborhoodSelect = (neighborhood: Neighborhood) => {
    if (onNeighborhoodChange) onNeighborhoodChange(neighborhood.name);
    setNeighborhoodSearch('');
    setShowNeighborhoodDropdown(false);
  };

  const selectClass = `relative flex items-center gap-2 px-3 py-2.5 rounded-xl border border-border text-sm font-medium text-foreground hover:border-primary/50 cursor-pointer transition-all bg-white ${compact ? 'py-2' : ''}`;
  const dropdownClass = 'absolute top-full left-0 mt-1 bg-white rounded-xl shadow-xl border border-border z-50 overflow-hidden animate-fade-in min-w-[200px] max-w-[280px]';

  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {/* Province */}
      <div ref={provinceRef} className="relative">
        <button
          type="button"
          onClick={() => { setShowProvinceDropdown(!showProvinceDropdown); setShowDistrictDropdown(false); setShowNeighborhoodDropdown(false); }}
          className={selectClass}
        >
          <MapPin size={14} className="text-primary flex-shrink-0" />
          <span className={selectedProvince ? 'text-foreground' : 'text-muted-foreground'}>
            {selectedProvince || 'İl seçin'}
          </span>
          {selectedProvince ? (
            <X size={12} className="text-muted-foreground hover:text-danger ml-1" onClick={(e) => { e.stopPropagation(); onProvinceChange(''); onDistrictChange(''); if (onNeighborhoodChange) onNeighborhoodChange(''); }} />
          ) : (
            <ChevronDown size={14} className="text-muted-foreground ml-1" />
          )}
        </button>
        {showProvinceDropdown && (
          <div className={dropdownClass} style={{ width: 220 }}>
            <div className="p-2 border-b border-border">
              <div className="relative">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  autoFocus
                  type="text"
                  value={provinceSearch}
                  onChange={(e) => setProvinceSearch(e.target.value)}
                  placeholder="İl ara..."
                  className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border border-border focus:outline-none focus:border-primary"
                />
              </div>
            </div>
            <div className="max-h-52 overflow-y-auto">
              {loadingProvinces ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 size={16} className="animate-spin text-primary" />
                </div>
              ) : filteredProvinces.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-3">Sonuç bulunamadı</p>
              ) : (
                filteredProvinces.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => handleProvinceSelect(p)}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors hover:bg-primary-light hover:text-primary ${selectedProvince === p.name ? 'bg-primary-light text-primary font-semibold' : 'text-foreground'}`}
                  >
                    {p.name}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* District */}
      {selectedProvince && (
        <div ref={districtRef} className="relative">
          <button
            type="button"
            onClick={() => { setShowDistrictDropdown(!showDistrictDropdown); setShowProvinceDropdown(false); setShowNeighborhoodDropdown(false); }}
            className={selectClass}
            disabled={loadingDistricts}
          >
            {loadingDistricts ? (
              <Loader2 size={14} className="animate-spin text-primary flex-shrink-0" />
            ) : (
              <MapPin size={14} className="text-muted-foreground flex-shrink-0" />
            )}
            <span className={selectedDistrict ? 'text-foreground' : 'text-muted-foreground'}>
              {selectedDistrict || 'İlçe seçin'}
            </span>
            {selectedDistrict ? (
              <X size={12} className="text-muted-foreground hover:text-danger ml-1" onClick={(e) => { e.stopPropagation(); onDistrictChange(''); if (onNeighborhoodChange) onNeighborhoodChange(''); }} />
            ) : (
              <ChevronDown size={14} className="text-muted-foreground ml-1" />
            )}
          </button>
          {showDistrictDropdown && !loadingDistricts && (
            <div className={dropdownClass} style={{ width: 220 }}>
              <div className="p-2 border-b border-border">
                <div className="relative">
                  <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    autoFocus
                    type="text"
                    value={districtSearch}
                    onChange={(e) => setDistrictSearch(e.target.value)}
                    placeholder="İlçe ara..."
                    className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border border-border focus:outline-none focus:border-primary"
                  />
                </div>
              </div>
              <div className="max-h-52 overflow-y-auto">
                {filteredDistricts.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-3">Sonuç bulunamadı</p>
                ) : (
                  filteredDistricts.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => handleDistrictSelect(d)}
                      className={`w-full text-left px-3 py-2 text-sm transition-colors hover:bg-primary-light hover:text-primary ${selectedDistrict === d.name ? 'bg-primary-light text-primary font-semibold' : 'text-foreground'}`}
                    >
                      {d.name}
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Neighborhood */}
      {selectedDistrict && onNeighborhoodChange && neighborhoods.length > 0 && (
        <div ref={neighborhoodRef} className="relative">
          <button
            type="button"
            onClick={() => { setShowNeighborhoodDropdown(!showNeighborhoodDropdown); setShowProvinceDropdown(false); setShowDistrictDropdown(false); }}
            className={selectClass}
            disabled={loadingNeighborhoods}
          >
            {loadingNeighborhoods ? (
              <Loader2 size={14} className="animate-spin text-primary flex-shrink-0" />
            ) : (
              <MapPin size={14} className="text-muted-foreground flex-shrink-0" />
            )}
            <span className={selectedNeighborhood ? 'text-foreground' : 'text-muted-foreground'}>
              {selectedNeighborhood || 'Mahalle seçin'}
            </span>
            {selectedNeighborhood ? (
              <X size={12} className="text-muted-foreground hover:text-danger ml-1" onClick={(e) => { e.stopPropagation(); onNeighborhoodChange(''); }} />
            ) : (
              <ChevronDown size={14} className="text-muted-foreground ml-1" />
            )}
          </button>
          {showNeighborhoodDropdown && !loadingNeighborhoods && (
            <div className={dropdownClass} style={{ width: 220 }}>
              <div className="p-2 border-b border-border">
                <div className="relative">
                  <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    autoFocus
                    type="text"
                    value={neighborhoodSearch}
                    onChange={(e) => setNeighborhoodSearch(e.target.value)}
                    placeholder="Mahalle ara..."
                    className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border border-border focus:outline-none focus:border-primary"
                  />
                </div>
              </div>
              <div className="max-h-52 overflow-y-auto">
                {filteredNeighborhoods.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-3">Sonuç bulunamadı</p>
                ) : (
                  filteredNeighborhoods.map((n) => (
                    <button
                      key={n.id}
                      type="button"
                      onClick={() => handleNeighborhoodSelect(n)}
                      className={`w-full text-left px-3 py-2 text-sm transition-colors hover:bg-primary-light hover:text-primary ${selectedNeighborhood === n.name ? 'bg-primary-light text-primary font-semibold' : 'text-foreground'}`}
                    >
                      {n.name}
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
